import { queryOptions } from '@tanstack/react-query'
import { apiClient, MEAL_PLANNER_API_URL } from '.'
import {
  DayOfWeek,
  MacrosDistribution,
  MealOfDayDivision,
  RecipeIDObject,
  RecommenderSettings,
  ScaledRecipe,
  SimulatePayload,
  SimulationProfile,
  UserNutritionalNeeds,
  UserProfile,
  WeekPlan,
} from './types'
import { AxiosResponse } from 'axios'

export async function getMealOfDayDivisions(): Promise<MealOfDayDivision[]> {
  return apiClient.get(`/mealOfDayDivision`).then((res) => res.data)
}

export const mealOfDayDivisionsQuery = queryOptions({
  queryKey: ['mealOfDayDivisions'],
  queryFn: getMealOfDayDivisions,
})

export async function simulate(payload: SimulatePayload): Promise<WeekPlan> {
  return apiClient
    .post(`/simulate`, payload, {
      baseURL: MEAL_PLANNER_API_URL,
    })
    .then((res) => res.data)
}

export async function getReplacements(
  userProfile: UserProfile,
  weekPlan: WeekPlan,
  mealOfDayDivisionToReplace: MealOfDayDivision,
  dayOfWeekToReplace: DayOfWeek,
  recipeToReplace: RecipeIDObject,
  recommenderSettings?: RecommenderSettings,
): Promise<ScaledRecipe[]> {
  return apiClient
    .post(
      `/simulate/recipeReplacements`,
      {
        userProfile,
        weekPlan,
        mealOfDayDivisionToReplace,
        dayOfWeekToReplace,
        recipeToReplace,
        recommenderSettings,
      },
      {
        baseURL: MEAL_PLANNER_API_URL,
      },
    )
    .then((res) => res.data)
}

export async function getRequiredMeals(): Promise<any> {
  return apiClient.get(`/energy-distribution/required-meals`).then((res) => res.data)
}

export async function getNutritionalNeeds(profile: UserProfile): Promise<UserNutritionalNeeds> {
  return apiClient
    .post(`/simulate/nutritionalNeeds`, profile, {
      baseURL: MEAL_PLANNER_API_URL,
    })
    .then((res) => res.data)
}

export const nutritionalNeedsQuery = (query: UserProfile) => {
  return queryOptions({
    queryKey: ['nutritionalNeeds', query],
    queryFn: () => getNutritionalNeeds(query),
  })
}

export async function getUserNutritionalNeeds(
  dbContext: string,
  id: string,
): Promise<UserNutritionalNeeds> {
  return apiClient
    .get(`/nutritionalNeeds`, {
      baseURL: MEAL_PLANNER_API_URL,
      headers: {
        'X-IMPERSONATE-USER-DB-CONTEXT': dbContext,
        'X-IMPERSONATE-USER-ID': id,
      },
    })
    .then((res) => res.data)
}

export const userNutritionalNeedsQuery = (dbContext: string, id: string) => {
  return queryOptions({
    queryKey: ['userNutritionalNeeds', dbContext, id],
    queryFn: () => getUserNutritionalNeeds(dbContext, id),
  })
}

export async function getUserMealPlan(
  dbContext: string,
  id: string,
  date: string,
): Promise<WeekPlan | undefined> {
  return apiClient
    .get(`/${date}`, {
      baseURL: MEAL_PLANNER_API_URL,
      headers: {
        'X-IMPERSONATE-USER-DB-CONTEXT': dbContext,
        'X-IMPERSONATE-USER-ID': id,
      },
      params: {
        model: 'DIETITIAN',
      },
    })
    .then((res) => res.data)
}

export const userMealPlanQuery = (dbContext: string, id: string, date: string) => {
  return queryOptions({
    queryKey: ['userMealPlan', dbContext, id, date],
    queryFn: () => getUserMealPlan(dbContext, id, date),
  })
}

export async function getEnergyDistribution(): Promise<MacrosDistribution> {
  return apiClient.get(`/energy-distribution`).then((res) => res.data)
}

export const energyDistributionQuery = queryOptions({
  queryKey: ['energyDistribution'],
  queryFn: getEnergyDistribution,
})

export async function saveSimulationProfile(payload: SimulationProfile) {
  return apiClient
    .post(
      `/simulate/profile`,
      { ...payload, userProfile: JSON.stringify(payload.userProfile) },
      {
        baseURL: MEAL_PLANNER_API_URL,
      },
    )
    .then((res) => res.data)
}

export async function updateSimulationProfile(payload: SimulationProfile) {
  return apiClient
    .put(
      `/simulate/profile/${payload.id}`,
      { ...payload, userProfile: JSON.stringify(payload.userProfile) },
      {
        baseURL: MEAL_PLANNER_API_URL,
      },
    )
    .then((res) => res.data)
}

export async function getSimulationProfilesList(): Promise<SimulationProfile[]> {
  return apiClient
    .get(`/simulate/profile`, {
      baseURL: MEAL_PLANNER_API_URL,
    })
    .then((res) => res.data)
}

export const simulationProfilesQuery = queryOptions({
  queryKey: ['simulationProfiles'],
  queryFn: getSimulationProfilesList,
})

export async function getSimulationProfile(id: number): Promise<SimulationProfile> {
  return apiClient
    .get(`/simulate/profile/${id}`, {
      baseURL: MEAL_PLANNER_API_URL,
    })
    .then((res) => {
      return { ...res.data, userProfile: JSON.parse(res.data.userProfile) }
    })
}

export const simulationProfileQuery = (id?: number) => {
  return queryOptions({
    queryKey: ['simulationProfile', id],
    queryFn: () => getSimulationProfile(id!),
    enabled: id !== undefined,
  })
}

export async function deleteSimulationProfile(id: number): Promise<AxiosResponse> {
  return apiClient
    .delete(`/simulate/profile/${id}`, {
      baseURL: MEAL_PLANNER_API_URL,
    })
    .then((res) => res.data)
}
